
import { Spin } from "antd";
import classNames from "classnames";
import Button from "@Components/Button";
import { NUMBER_BUTTON } from "@Configs/enum";
import React, { ReactNode, RefObject } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import "./Drawer.scss";
import Close24 from "assets/icons/Close24";
import { CommonService } from "@Services/common-service";
import { calc } from "antd/es/theme/internal";

const antIcon = (
  <LoadingOutlined style={{ fontSize: 24, color: "#0f62fe" }} spin />
);

export interface DrawerProps {
  children?: ReactNode;
  /**Control the drawer visible or not*/
  visible?: boolean;
  /**Control the footer of drawer visible or not*/
  visibleFooter?: boolean;
  /**Control the disable state of Apply button and Apply button*/
  disableButton?: boolean;
  /**Control the disable state of Cancel button*/
  disableButtonCancel?: boolean;
  /**Control the disable state of Cancel button*/
  disableButtonDelete?: boolean;
  /**Control the loading state of the drawer*/
  loading?: boolean;
  /**The title for drawer*/
  title?: ReactNode;
  /**The description for title*/
  description?: ReactNode;
  /**The title of Apply button*/
  titleButtonApply?: string;
  /**The title of Delete button*/
  titleButtonDelete?: string;
  /**The title of ApplyNext button*/
  titleButtonApplyNext?: string;
  /**The title of cancel button*/
  titleButtonCancel?: string;
  /**Control number button of footer drawer*/
  numberButton?: NUMBER_BUTTON;
  /**Function to be called when user click close icon*/
  handleClose?: () => void;
  /**Function to be called when user click cancel button*/
  handleCancel?: () => void;
  /**Function to be called when user click delete button*/
  handleDelete?: () => void;
  /**Function to be called when user click Apply button. After click the drawer will be closed*/
  handleSave?: () => void;
  /**Function to be called when user click submit button ApplyNext*/
  handleApplyNext?: () => void;
  /**Control close icon visible or not*/
  isHaveCloseIcon?: boolean;
  /**Control the size of drawer*/
  size?: "sm" | "lg" | "xl" | "2xl" | "max";
  /**Control the size of drawer*/
  side?: "left" | "right";
  /**Use to custom style the component*/
  className?: string;
  /**Option to control overlay drawer visible*/
  hasOverlay?: boolean
  /**Should handle close when click outside */
  shouldCloseWhenClickOutSide?: boolean;
  /**Show button cancel */
  isShowButtonDelete?: boolean;
  /**Show button cancel */
  isShowButtonCancel?: boolean;
  /**Show button apply next*/
  isShowButtonApplyNext?: boolean;
  /**Show button apply*/
  isShowButtonApply?: boolean;

  footer?: ReactNode;

  overlayClassName?: string;
}

function Drawer(props: DrawerProps) {
  const {
    visibleFooter = true,
    disableButton = false,
    disableButtonCancel = false,
    disableButtonDelete = false,
    numberButton = NUMBER_BUTTON.TWO,
    isHaveCloseIcon = true,
    size = "sm",
    side = "right",
    visible,
    loading,
    title,
    description,
    titleButtonApply,
    titleButtonApplyNext,
    titleButtonCancel,
    titleButtonDelete,
    handleClose,
    handleCancel,
    handleDelete,
    handleSave,
    handleApplyNext,
    hasOverlay = true,
    className,
    shouldCloseWhenClickOutSide = false,
    isShowButtonCancel = true,
    isShowButtonApplyNext = false,
    isShowButtonDelete = false,
    isShowButtonApply = true,
    footer,
    overlayClassName = "",
  } = props;

  const ref: RefObject<HTMLDivElement> = React.useRef<HTMLDivElement>(null);

  const defaultWidth = size === "sm" ? 400 : size === "lg" ? 800 : size === "xl" ? 1200 : size === "2xl" ? 1300 : window.screen.width * 0.9;
  const [width, setWidth] = React.useState(defaultWidth); // Chiều rộng mặc định
  const isResizing = React.useRef(false);

  const minWidth = 400;
  const maxWidth = window.screen.width * 0.9;

  const handleMouseDown = () => {
    isResizing.current = true;
    document.body.style.cursor = 'ew-resize';
  };

  const handleMouseUp = () => {
    isResizing.current = false;
    document.body.style.cursor = 'default';
  };

  const handleMouseMove = (e) => {
    if (!isResizing.current) return;
    const newWidth = window.innerWidth - e.clientX;
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setWidth(newWidth);
    }
  };

  // Đăng ký sự kiện chuột toàn cục khi mở Drawer
  React.useEffect(() => {
    if (open) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [open]);

  CommonService.useClickOutside(ref, handleClose);

  const renderModalFooter = React.useMemo(
    () => (
      <div className="button-footer">
        {isShowButtonDelete && (
          <Button
            type="text"
            onClick={handleDelete}
            disabled={disableButtonDelete}
          >
            <span>{titleButtonDelete ? titleButtonDelete : "Delete"}</span>
          </Button>
        )}
        {isShowButtonCancel && (
          <Button
            type="secondary"
            onClick={handleCancel}
            disabled={disableButtonCancel}
          >
            <span>{titleButtonCancel ? titleButtonCancel : "Cancel"}</span>
          </Button>
        )}

        {numberButton === NUMBER_BUTTON.THREE && isShowButtonApplyNext && (
          <Button
            type="secondary"
            onClick={handleApplyNext}
            disabled={disableButton}
          >
            <span>
              {titleButtonApplyNext ? titleButtonApplyNext : "Apply Next"}
            </span>
          </Button>
        )}
        {isShowButtonApply && (
          <Button
            type="primary"
            onClick={handleSave}
            disabled={disableButton}
          >
            <span>{titleButtonApply ? titleButtonApply : "Apply"}</span>
          </Button>
        )}

      </div>
    ),
    [
      numberButton,
      handleSave,
      disableButton,
      titleButtonApply,
      handleApplyNext,
      titleButtonApplyNext,
      handleCancel,
      titleButtonCancel,
    ]
  );
  return (
    <>

      <div
        className={classNames(
          `drawer__container drawer-${size} side-${side}`,
          {
            "slide-out": visible === true,
          },
          className
        )}
        ref={shouldCloseWhenClickOutSide ? ref : undefined}
        style={{ width: visible && width ? width : visible && width === undefined ? "unset" : 0 }}
      >
        <Spin spinning={loading} indicator={antIcon}>
          <div className="drawer__content">
            <div className="drawer__header">
              <div
                className={classNames("drawer__header-text", {
                  "have-description": description,
                })}
              >
                <div className="title mr-1">
                  {title ? title : "Drawer Title"}
                </div>
                {description && (
                  <div className="description mr-1">{description}</div>
                )}
              </div>
              {!description && isHaveCloseIcon && (
                <div className="button-close">
                  <Close24 onClick={handleClose} />
                </div>
              )}
            </div>

            <div className={classNames(`drawer__body`)} >
              {props.children}
            </div>
            {visibleFooter && !footer && (
              <div className="drawer_footer">{renderModalFooter}</div>
            )}
            {visibleFooter && footer && (
              <div className="drawer_footer">{footer}</div>
            )}
          </div>
        </Spin>
        {/* Thanh kéo để resize */}
        <div
          onMouseDown={handleMouseDown}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: '5px',
            cursor: 'ew-resize',
            zIndex: 10000,
          }}
        />
      </div>
      {visible && <div className={classNames({
        overlay: hasOverlay
      }, { overlayClassName })}></div>}

    </>
  );
}

export default Drawer;
